import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertCircle,
  FiEye,
  FiMonitor,
  FiRefreshCw,
  FiSave,
  FiSmartphone,
} from 'react-icons/fi';
import api from '../../services/api';
import '../../components/Button.css';
import MarketingPopupPreview from './MarketingPopupPreview';
import MarketingPopupCopyTab from './MarketingPopupCopyTab';
import { resolveCopyEditSection } from './marketingPopupCopySections';
import MarketingPopupBehaviorTab from './MarketingPopupBehaviorTab';
import MarketingPopupKlantenTab from './MarketingPopupKlantenTab';
import MarketingXendySyncLogTab from './MarketingXendySyncLogTab';
import Klanten360Modal from '../../components/klanten/Klanten360Modal';
import CollapsibleHelp from '../../components/CollapsibleHelp';
import { useKlanten360Modal } from '../../hooks/useKlanten360Modal';
import './MarketingPopupPanel.css';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidMailListUuid(value) {
  return UUID_RE.test(String(value || '').trim());
}

const TABS = [
  { id: 'copy', label: 'Tekst & aanbod' },
  { id: 'rules', label: 'Timing' },
  { id: 'behavior', label: 'Gedrag' },
  { id: 'klanten', label: 'Klanten' },
  { id: 'xendy', label: 'Xendy' },
  { id: 'sync-log', label: 'Sync-log' },
  { id: 'test', label: 'Test shop' },
];

export default function MarketingPopupPanel() {
  const [config, setConfig] = useState(null);
  const [xendy, setXendy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState('copy');
  const [copyEditSection, setCopyEditSection] = useState('welcome');
  const [previewVariant, setPreviewVariant] = useState('welcome');
  const [previewPhase, setPreviewPhase] = useState('form');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  /** null = standaard teksten; anders gedragsscenario in preview */
  const [previewBehavior, setPreviewBehavior] = useState(null);
  const [testVisitorId, setTestVisitorId] = useState('');
  const [testResetEmail, setTestResetEmail] = useState('');
  const [resetSummary, setResetSummary] = useState(null);
  const [diagnose, setDiagnose] = useState(null);
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const [audience, setAudience] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(true);
  const [audienceError, setAudienceError] = useState('');
  const [xendyTest, setXendyTest] = useState(null);
  const [xendyTestLoading, setXendyTestLoading] = useState(false);
  const [xendyCreateLoading, setXendyCreateLoading] = useState(false);
  const [xendyResyncLoading, setXendyResyncLoading] = useState(false);
  const [xendyResyncResult, setXendyResyncResult] = useState(null);
  const klanten360Modal = useKlanten360Modal();

  const loadAudience = useCallback(async () => {
    setAudienceLoading(true);
    setAudienceError('');
    try {
      const res = await api.get('/marketing/popup/audience-week?days=7');
      setAudience(res.data);
    } catch (e) {
      setAudienceError(e.response?.data?.error || e.message);
    } finally {
      setAudienceLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [cfg] = await Promise.all([
        api.get('/marketing/popup/config'),
        loadAudience(),
      ]);
      setConfig(cfg.data?.config || {});
      setXendy(cfg.data?.xendy || {});
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [loadAudience]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === 'behavior' && config?.behavior_enabled && previewBehavior == null) {
      setPreviewBehavior('cart_abandon');
    }
  }, [tab, config?.behavior_enabled, previewBehavior]);

  const resolvePreviewVariant = useCallback(
    (section) => {
      if (section?.id !== 'returning') return section?.previewVariant || 'welcome';
      const hasOffer =
        (parseFloat(config?.returning_coupon_fixed_eur) || 0) > 0 || (config?.returning_coupon_percent || 0) > 0;
      return hasOffer ? 'returning_offer' : 'newsletter';
    },
    [config?.returning_coupon_fixed_eur, config?.returning_coupon_percent]
  );

  useEffect(() => {
    if (tab !== 'copy') return;
    const { section, phase } = resolveCopyEditSection(copyEditSection);
    if (section.id !== copyEditSection) {
      setCopyEditSection(section.id);
      setPreviewVariant(resolvePreviewVariant(section));
      setPreviewPhase(phase || 'form');
      setPreviewBehavior(null);
      return;
    }
    setPreviewVariant(resolvePreviewVariant(section));
    if (!section.hasThanks) setPreviewPhase('form');
    setPreviewBehavior(null);
  }, [tab, copyEditSection, resolvePreviewVariant]);

  const handleCopyEditSection = (id) => {
    const { section } = resolveCopyEditSection(id);
    setCopyEditSection(section.id);
    setPreviewVariant(resolvePreviewVariant(section));
    setPreviewPhase('form');
    setPreviewBehavior(null);
  };

  const handleCopyEditPhase = (phase) => {
    setPreviewPhase(phase === 'success' ? 'success' : 'form');
    setPreviewBehavior(null);
  };

  const configRef = useRef(config);
  const xendyRef = useRef(xendy);
  const autosaveReady = useRef(false);
  configRef.current = config;
  xendyRef.current = xendy;

  const persistConfig = useCallback(async () => {
    const current = configRef.current;
    if (!current) return;
    setSaving(true);
    setError('');
    setSaveState('');
    try {
      const res = await api.put('/marketing/popup/config', {
        config: current,
        xendy: xendyRef.current,
      });
      if (res.data?.xendy) setXendy(res.data.xendy);
      setSaveState('Opgeslagen');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setSaveState('');
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    if (!config) return undefined;
    if (!autosaveReady.current) {
      autosaveReady.current = true;
      return undefined;
    }
    setSaveState('Nog niet opgeslagen');
    const timer = setTimeout(() => {
      persistConfig();
    }, 700);
    return () => clearTimeout(timer);
  }, [config, persistConfig]);

  const save = () => {
    persistConfig();
  };

  const previewConfig = useMemo(() => config || {}, [config]);

  const exampleCode =
    previewVariant === 'returning_offer'
      ? `${previewConfig.returning_coupon_prefix || 'TERUG'}-A1B2C3`
      : `${previewConfig.coupon_prefix || 'WELKOM'}-A1B2C3`;

  const runDiagnose = async () => {
    setDiagnoseLoading(true);
    setDiagnose(null);
    try {
      const res = await api.post('/marketing/popup/diagnose', {
        visitor_id: testVisitorId.trim(),
        path: '/',
      });
      setDiagnose(res.data);
    } catch (e) {
      setDiagnose({ show: false, hint: e.response?.data?.error || e.message });
    } finally {
      setDiagnoseLoading(false);
    }
  };

  const runXendyTest = async () => {
    setXendyTestLoading(true);
    setXendyTest(null);
    try {
      const res = await api.get('/marketing/popup/xendy/test');
      setXendyTest(res.data);
    } catch (e) {
      setXendyTest({ ok: false, error: e.response?.data?.error || e.message });
    } finally {
      setXendyTestLoading(false);
    }
  };

  const resyncXendySubscribers = async () => {
    setXendyResyncLoading(true);
    setXendyResyncResult(null);
    try {
      const res = await api.post('/marketing/popup/xendy/resync-failed', { limit: 50 });
      setXendyResyncResult(res.data);
      loadAudience();
    } catch (e) {
      setXendyResyncResult({ error: e.response?.data?.error || e.message });
    } finally {
      setXendyResyncLoading(false);
    }
  };

  const createXendyMailList = async () => {
    setXendyCreateLoading(true);
    try {
      const res = await api.post('/marketing/popup/xendy/mail-list', {
        name: '4shine pop-up inschrijvingen',
        description: 'Inkoop Software — pop-up en footer nieuwsbrief',
      });
      if (res.data?.list_id) {
        setXendy((x) => ({ ...x, list_id: res.data.list_id }));
      }
      if (res.data?.xendy) {
        setXendy(res.data.xendy);
      }
      await runXendyTest();
    } catch (e) {
      setXendyTest({ ok: false, error: e.response?.data?.error || e.message });
    } finally {
      setXendyCreateLoading(false);
    }
  };

  const resetTestVisitor = async () => {
    if (!testVisitorId.trim() && !testResetEmail.trim()) return;
    setDiagnoseLoading(true);
    setResetSummary(null);
    try {
      const res = await api.post('/marketing/popup/reset-visitor', {
        visitor_id: testVisitorId.trim() || undefined,
        email: testResetEmail.trim() || undefined,
      });
      setResetSummary(res.data);
      if (testVisitorId.trim()) {
        await runDiagnose();
      } else {
        setDiagnose(null);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setDiagnoseLoading(false);
    }
  };

  /** Ontkoppel browser van klant-e-mail (orders/inschrijvingen blijven). */
  const unlinkTestVisitor = async () => {
    if (!testVisitorId.trim()) return;
    setDiagnoseLoading(true);
    setResetSummary(null);
    try {
      const res = await api.post('/marketing/popup/unlink-visitor', {
        visitor_id: testVisitorId.trim(),
        email: testResetEmail.trim() || undefined,
      });
      setResetSummary({ ...res.data, mode: 'unlink' });
      await runDiagnose();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setDiagnoseLoading(false);
    }
  };

  if (loading && !config) {
    return <p className="muted">Pop-up laden…</p>;
  }

  return (
    <div className="marketing-popup-panel">
      <header className="marketing-popup-top">
        <div>
          <h2 className="marketing-popup-title">Pop-up 4shine</h2>
          <CollapsibleHelp title="Over pop-up & deploy" className="collapsible-help--compact">
            <p>
              Plugin v1.3.10 · preview rechts is niet live. Opslaan → Railway. Pop-up op shop: actief + kopers +{' '}
              {config?.delay_seconds ?? 12}s wachten. Footer nieuwsbrief: shortcode{' '}
              <code>[inkoop_footer_newsletter]</code> in WordPress (vervang oude footer-form).
            </p>
          </CollapsibleHelp>
        </div>
        <div className="marketing-popup-top-actions">
          <div className="marketing-popup-live-flags">
            <span className={`marketing-popup-status ${config?.enabled ? 'on' : 'off'}`}>
              {config?.enabled ? 'Live' : 'Uit'}
            </span>
            {!config?.enabled && config?.lead_capture_enabled ? (
              <span className="marketing-popup-status on">Verzamelen</span>
            ) : null}
            {config?.enabled ? (
              <>
                <span className={`marketing-popup-status ${config?.show_for_returning_customers ? 'on' : 'off'}`}>
                  Kopers {config?.show_for_returning_customers ? 'aan' : 'uit'}
                </span>
                <span className={`marketing-popup-status ${config?.behavior_enabled ? 'on' : 'off'}`}>
                  Gedrag {config?.behavior_enabled ? 'aan' : 'uit'}
                </span>
              </>
            ) : null}
          </div>
          {saveState ? <span className="muted marketing-popup-save-state">{saveState}</span> : null}
          <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
            <FiSave /> {saving ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </header>

      {error ? <p className="marketing-ig-error">{error}</p> : null}

      {config && !config.enabled && !config.lead_capture_enabled ? (
        <div className="marketing-popup-alert" role="status">
          <FiAlertCircle aria-hidden /> Zet <strong>Pop-up actief</strong> aan, of alleen{' '}
          <strong>e-mail verzamelen</strong> (tab Tekst) — daarna opslaan.
        </div>
      ) : null}

      {config && !config.enabled && config.lead_capture_enabled ? (
        <div className="marketing-popup-alert marketing-popup-alert--info" role="status">
          <FiAlertCircle aria-hidden /> Verzamelfase: pop-up staat aan zonder kortingscodes. Inschrijvingen verschijnen in
          Klanten-tab; koppel Xendy later in tab Xendy.
        </div>
      ) : null}

      {config?.enabled && !config?.show_for_returning_customers ? (
        <div className="marketing-popup-alert" role="status">
          <FiAlertCircle aria-hidden /> Console <code>returning_customer</code>? Zet{' '}
          <button type="button" className="marketing-popup-link-btn" onClick={() => setTab('copy')}>
            bekende kopers
          </button>{' '}
          aan.
        </div>
      ) : null}

      <div className="marketing-popup-layout">
        <div className="marketing-popup-editor">
          <nav className="marketing-popup-tabs" aria-label="Pop-up instellingen">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={tab === t.id ? 'active' : ''}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {t.id === 'klanten' && audience?.total != null ? (
                  <span className="marketing-popup-tab-count">{audience.total}</span>
                ) : null}
              </button>
            ))}
          </nav>

          <div className="marketing-popup-tab-panel">
            {tab === 'copy' && (
              <MarketingPopupCopyTab
                config={config}
                setConfig={setConfig}
                editSection={copyEditSection}
                editPhase={previewPhase}
                onEditSectionChange={handleCopyEditSection}
                onEditPhaseChange={handleCopyEditPhase}
              />
            )}
            {tab === 'behavior' && <MarketingPopupBehaviorTab config={config} setConfig={setConfig} />}
            {tab === 'klanten' && (
              <MarketingPopupKlantenTab
                data={audience}
                loading={audienceLoading}
                error={audienceError}
                onRefresh={loadAudience}
                onOpen360={klanten360Modal.open360}
              />
            )}

            {tab === 'sync-log' && (
              <MarketingXendySyncLogTab onOpen360={klanten360Modal.open360} />
            )}

            {tab === 'rules' && (
              <>
                <CollapsibleHelp title="Timing & codes">
                  <p>
                    <strong>Timing</strong> (delay, opnieuw tonen, cooldown) geldt voor <strong>alle</strong> bezoekers.
                    Nieuwe bezoekers zien welcome; bekende kopers (indien aan) een <strong>andere</strong> tekst/aanbod
                    (tab Teksten). Max 1× per browsersessie. Geen pop-up op cart/checkout.
                  </p>
                </CollapsibleHelp>

                <h3 className="mpp-rules-heading">Pop-up timing (iedereen)</h3>
                <p className="mpp-field-hint mpp-rules-lead">
                  Delay en cooldown gelden voor nieuwe bezoekers én bekende kopers — niet alleen voor kopers.
                </p>
                <div className="marketing-popup-grid">
                  <label>
                    Delay (sec)
                    <input
                      type="number"
                      className="form-input"
                      min={0}
                      max={120}
                      value={config?.delay_seconds ?? 12}
                      onChange={(e) => setConfig((c) => ({ ...c, delay_seconds: Number(e.target.value) }))}
                    />
                  </label>
                  <label>
                    Opnieuw tonen (dagen)
                    <input
                      type="number"
                      className="form-input"
                      min={1}
                      max={90}
                      value={config?.min_days_between_shows ?? 7}
                      onChange={(e) => setConfig((c) => ({ ...c, min_days_between_shows: Number(e.target.value) }))}
                    />
                  </label>
                  <label>
                    Cooldown na sluiten (dagen)
                    <input
                      type="number"
                      className="form-input"
                      min={1}
                      max={90}
                      value={config?.dismiss_cooldown_days ?? 7}
                      onChange={(e) => setConfig((c) => ({ ...c, dismiss_cooldown_days: Number(e.target.value) }))}
                    />
                  </label>
                </div>

                <h3 className="mpp-rules-heading">Welkomstcode (nieuwe bezoekers)</h3>
                <div className="marketing-popup-grid">
                  <label>
                    Code geldig (dagen)
                    <input
                      type="number"
                      className="form-input"
                      min={1}
                      max={60}
                      value={config?.coupon_valid_days ?? 14}
                      onChange={(e) => setConfig((c) => ({ ...c, coupon_valid_days: Number(e.target.value) }))}
                    />
                  </label>
                  <label>
                    Prefix welcome
                    <input
                      className="form-input"
                      value={config?.coupon_prefix || 'WELKOM'}
                      onChange={(e) => setConfig((c) => ({ ...c, coupon_prefix: e.target.value }))}
                    />
                  </label>
                </div>

                <h3 className="mpp-rules-heading">Bekende kopers</h3>
                <label className="marketing-ig-checkbox mpp-card-toggle">
                  <input
                    type="checkbox"
                    checked={!!config?.show_for_returning_customers}
                    onChange={(e) => setConfig((c) => ({ ...c, show_for_returning_customers: e.target.checked }))}
                  />
                  Pop-up ook tonen aan bekende kopers
                </label>
                <span className="mpp-field-hint">
                  Uit = alleen nieuwe bezoekers (welcome). Aan = bestaande klanten zien een{' '}
                  <strong>andere</strong> pop-up (terugkerend-aanbod / nieuwsbrief), niet de WELKOM-code. Teksten in tab
                  Teksten.
                </span>

                <h3 className="mpp-rules-heading">Browse follow-up mail (Xendy)</h3>
                <CollapsibleHelp title="Productvelden & tags" className="collapsible-help--compact">
                  <p>
                    Bij inschrijving/resync: <code>cart_product_*</code> (mandje) en <code>browse_product_*</code>{' '}
                    (bekeken) apart. Tags: <code>lead_with_cart</code> / <code>lead_with_browse</code>. Later na X uur
                    met <code>browse_followup</code>.
                  </p>
                </CollapsibleHelp>
                <label className="marketing-ig-checkbox mpp-card-toggle">
                  <input
                    type="checkbox"
                    checked={config?.browse_followup_enabled !== false}
                    onChange={(e) => setConfig((c) => ({ ...c, browse_followup_enabled: e.target.checked }))}
                  />
                  Browse follow-up mail (Xendy)
                </label>
                <div className="marketing-popup-grid">
                  <label>
                    Uren na laatste activiteit
                    <input
                      type="number"
                      className="form-input"
                      min={1}
                      max={72}
                      value={config?.browse_followup_hours ?? 1}
                      onChange={(e) => setConfig((c) => ({ ...c, browse_followup_hours: Number(e.target.value) }))}
                    />
                    <span className="mpp-field-hint">
                      Daarna sync + cart-lijst (standaard 1 = ~1 uur na weggaan). Zet in Xendy Cart 01 op{' '}
                      <strong>Direct / Immediate</strong>, niet “dagelijks 08:00”.
                    </span>
                  </label>
                  <label>
                    Max. producten in mail
                    <input
                      type="number"
                      className="form-input"
                      min={1}
                      max={3}
                      value={config?.browse_followup_max_products ?? 2}
                      onChange={(e) =>
                        setConfig((c) => ({ ...c, browse_followup_max_products: Number(e.target.value) }))
                      }
                    />
                  </label>
                  <label>
                    Mail-cooldown (dagen)
                    <input
                      type="number"
                      className="form-input"
                      min={1}
                      max={90}
                      value={config?.browse_followup_cooldown_days ?? 7}
                      onChange={(e) =>
                        setConfig((c) => ({ ...c, browse_followup_cooldown_days: Number(e.target.value) }))
                      }
                    />
                  </label>
                </div>
                <label>
                  Uitsluitingen (productnaam bevat…)
                  <textarea
                    className="form-input"
                    rows={2}
                    placeholder="fitting, fittingen"
                    value={
                      Array.isArray(config?.browse_followup_exclude)
                        ? config.browse_followup_exclude.join(', ')
                        : config?.browse_followup_exclude || ''
                    }
                    onChange={(e) => setConfig((c) => ({ ...c, browse_followup_exclude: e.target.value }))}
                  />
                  <span className="mpp-field-hint">
                    Komma of nieuwe regel. Voorbeeld: fitting, fittingen — die producten gaan niet naar Xendy.
                  </span>
                </label>
              </>
            )}

            {tab === 'xendy' && (
              <>
                <CollapsibleHelp title="Xendy koppeling & API-key">
                  <p>
                    Xendy API v1:{' '}
                    <a href="https://api.v2.xendy.me/v1/openapi.json" target="_blank" rel="noreferrer">
                      openapi.json
                    </a>
                    . Key in Xendy onder Instellingen → API keys; waarde als <code>XENDY_API_KEY</code> op Railway
                    (Bearer <code>xnd_live_…</code>).
                  </p>
                  <p>
                    Bij nieuwe API key: <strong>Contacten</strong> lezen + schrijven, <strong>Mail lists &amp;
                    audiences</strong> lezen (+ schrijven als je hier een lijst laat aanmaken). Versturen niet nodig.
                  </p>
                  <p>
                    <strong>Mail list vs segment:</strong> Inkoop koppelt aan een <code>mail_list</code> (UUID). Een
                    dynamisch segment (<code>audience</code>) uit het dashboard is <em>geen</em> mail list — die UUID
                    werkt niet in het veld hieronder (<code>Mail list not found</code>).
                  </p>
                  <p>
                    Geen mail list? Klik <strong>Maak mail list in Xendy</strong> (API{' '}
                    <code>POST /audiences</code>) of laat Mail list UUID <strong>leeg</strong>: contacten komen dan in
                    Alle contacten zonder lijst-koppeling.
                  </p>
                  <p>
                    Sync: <code>POST /contacts</code> met <code>mail_list</code>. Contacten staan in{' '}
                    <strong>Alle contacten</strong>; statisch segment &quot;4shine pop-up inschrijvingen&quot; vult pas
                    als sync slaagt. Dynamisch segment = eigen filterregels, geen API-doel.
                  </p>
                  <p>
                    Sync mislukt vaak door onbekende custom fields in Xendy (422) — Inkoop probeert daarna zonder
                    velden. Gebruik <strong>Sync inschrijvingen opnieuw</strong> na fix.
                  </p>
                </CollapsibleHelp>
                <CollapsibleHelp title="Welkomstmail met kortingscode (Xendy automation)">
                  <p>Inkoop stuurt <strong>geen</strong> e-mail zelf — dat doe je in Xendy via <strong>Email automations</strong>.</p>
                  <ol>
                    <li>
                      Maak in Xendy custom fields (exacte keys): <code>coupon_code</code>, optioneel{' '}
                      <code>popup_variant</code>, <code>popup_tags</code>.
                    </li>
                    <li>
                      Automation → trigger: contact komt op mail list <em>4shine pop-up inschrijvingen</em> (of tag{' '}
                      <code>popup_subscribed</code> / <code>welcome</code> als je tags in Xendy gebruikt).
                    </li>
                    <li>
                      E-mail: gebruik merge field voor <code>coupon_code</code> + voornaam. Code staat ook in pop-up op
                      de site; mail is backup/herinnering.
                    </li>
                    <li>
                      Naam: Inkoop vult <code>first_name</code> uit Woo als die e-mail al bestelde; anders blijft
                      “Geen naam” tot eerste order.
                    </li>
                  </ol>
                </CollapsibleHelp>
                <label className="marketing-ig-checkbox">
                  <input
                    type="checkbox"
                    checked={!!xendy?.enabled}
                    onChange={(e) => setXendy((x) => ({ ...x, enabled: e.target.checked }))}
                  />
                  Sync inschrijvingen naar Xendy
                </label>
                <label className="marketing-ig-checkbox">
                  <input
                    type="checkbox"
                    checked={xendy?.sync_behavior_signals !== false}
                    onChange={(e) => setXendy((x) => ({ ...x, sync_behavior_signals: e.target.checked }))}
                  />
                  Gedragssignalen naar Xendy (voor e-mailautomations)
                </label>
                <label>
                  Mail list UUID
                  <input
                    className="form-input mpp-uuid-input"
                    value={xendy?.list_id || ''}
                    onChange={(e) => setXendy((x) => ({ ...x, list_id: e.target.value.trim() }))}
                    placeholder="8eab78a6-64d8-4cd2-a491-7665b4164e40"
                    spellCheck={false}
                    autoComplete="off"
                  />
                </label>
                <label>
                  Extra lijst bij nieuwe inschrijving
                  <input
                    className="form-input mpp-uuid-input"
                    value={xendy?.newsletter_list_id || ''}
                    onChange={(e) => setXendy((x) => ({ ...x, newsletter_list_id: e.target.value.trim() }))}
                    placeholder="109325ee-618f-4cf0-96dd-2c3f2e3dcf40"
                    spellCheck={false}
                    autoComplete="off"
                  />
                </label>
                <p className="muted">
                  Elke nieuwe pop-up- of footer-inschrijving komt ook op deze lijst, naast de mail list hierboven.
                </p>
                <label>
                  Lijst achtergelaten winkelwagen
                  <input
                    className="form-input mpp-uuid-input"
                    value={xendy?.abandoned_cart_list_id || ''}
                    onChange={(e) =>
                      setXendy((x) => ({ ...x, abandoned_cart_list_id: e.target.value.trim() }))
                    }
                    placeholder="72671c83-6bad-4092-a2e6-d6b4701a695a"
                    spellCheck={false}
                    autoComplete="off"
                  />
                </label>
                <p className="muted">
                  Pas <strong>~1 uur na laatste site-activiteit</strong> (browse follow-up), niet bij inschrijving.
                  In Xendy moet de Cart-automation op <strong>Direct</strong> staan (niet periodiek om 08:00).
                </p>
                {xendy?.list_id && !isValidMailListUuid(xendy.list_id) ? (
                  <p className="marketing-ig-error mpp-uuid-warn" role="status">
                    UUID incompleet of ongeldig — plak de volledige id uit de Xendy-URL (36 tekens).
                  </p>
                ) : null}
                <p className="muted mpp-xendy-key-status">
                  API-key op server:{' '}
                  {xendy?.has_api_key ? (
                    <span className="marketing-popup-ok">gezet</span>
                  ) : (
                    <span>niet gezet — eerst Railway variable</span>
                  )}
                </p>
                <div className="marketing-popup-test-actions">
                  <button type="button" className="btn btn-secondary btn-sm" disabled={xendyTestLoading} onClick={runXendyTest}>
                    Test Xendy-verbinding
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={xendyCreateLoading}
                    onClick={createXendyMailList}
                  >
                    Maak mail list in Xendy
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={xendyResyncLoading || !xendy?.enabled}
                    onClick={resyncXendySubscribers}
                  >
                    Vul Xendy-velden opnieuw (o.a. coupon_code)
                  </button>
                </div>
                {xendyResyncResult ? (
                  <div className="marketing-popup-diagnose" role="status">
                    {xendyResyncResult.error ? (
                      <p className="marketing-ig-error">{xendyResyncResult.error}</p>
                    ) : (
                      <p className="muted">
                        Xendy opnieuw: {xendyResyncResult.synced}/{xendyResyncResult.attempted} gelukt
                        {xendyResyncResult.failed ? `, ${xendyResyncResult.failed} mislukt` : ''}
                        {xendyResyncResult.with_products != null
                          ? ` · producten gezet: ${xendyResyncResult.with_products}`
                          : ''}
                        . Kijk in Xendy onder <strong>Alle contacten</strong> (segment telt pas na regels).
                      </p>
                    )}
                    {Array.isArray(xendyResyncResult.product_samples) &&
                    xendyResyncResult.product_samples.length ? (
                      <ul className="marketing-popup-diagnose-list">
                        {xendyResyncResult.product_samples.slice(0, 5).map((s) => (
                          <li key={s.email}>
                            {s.email}:{' '}
                            {s.products?.length ? s.products.join(' · ') : 'geen producten gevonden'}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : null}
                {xendyTest ? (
                  <div className={`marketing-popup-diagnose ${xendyTest.ok ? 'ok' : 'block'}`} role="status">
                    <strong>{xendyTest.ok ? 'Xendy OK' : 'Xendy probleem'}</strong>
                    {xendyTest.error ? <p>{xendyTest.error}</p> : null}
                    {xendyTest.company ? <p className="muted">Account: {xendyTest.company}</p> : null}
                    {xendyTest.missing_scopes?.length ? (
                      <p>Ontbrekende scopes: {xendyTest.missing_scopes.join(', ')}</p>
                    ) : null}
                    {xendyTest.audiences_error ? (
                      <p className="marketing-ig-error">Audiences: {xendyTest.audiences_error}</p>
                    ) : null}
                    {xendyTest.mail_lists?.length ? (
                      <ul className="marketing-popup-diagnose-list">
                        {xendyTest.mail_lists.map((l) => (
                          <li key={l.id}>
                            {l.name} · {l.contact_count} contacten · <code>{l.id}</code>{' '}
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setXendy((x) => ({ ...x, list_id: l.id }))}
                            >
                              Gebruik UUID
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : xendyTest.ok ? (
                      <p className="muted">
                        Geen mail lists in Xendy — klik &quot;Maak mail list in Xendy&quot; of laat UUID leeg.
                      </p>
                    ) : null}
                    {xendyTest.dynamic_segments?.length ? (
                      <p className="muted">
                        Dynamische segmenten (niet als Mail list UUID):{' '}
                        {xendyTest.dynamic_segments.map((s) => s.name).join(', ')}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}

            {tab === 'test' && (
              <>
                <CollapsibleHelp title="Test op 4shine">
                  <p>
                    Console: filter <code>Inkoop Pop-up</code>. Visitor:{' '}
                    <code>localStorage.getItem(&apos;inkoop_vid&apos;)</code>
                  </p>
                  <p>
                    <strong>Ontkoppel browser</strong> — als jij ooit een klantorder met jouw browser hebt gedaan en
                    het systeem blijft die klant-e-mail herkennen. Orders en inschrijvingen van de klant blijven
                    bestaan; alleen de koppeling browser ↔ e-mail verdwijnt. Daarna op 4shine:{' '}
                    <code>localStorage.removeItem(&apos;inkoop_vid&apos;)</code> + hard refresh.
                  </p>
                  <p>
                    <strong>Visitor test reset</strong> wist ook pop-up-inschrijvingen (zwaarder, voor testdata).
                  </p>
                </CollapsibleHelp>
                <label>
                  visitor_id
                  <input
                    className="form-input"
                    value={testVisitorId}
                    onChange={(e) => setTestVisitorId(e.target.value)}
                    placeholder="v_…"
                  />
                </label>
                <label>
                  E-mail (optioneel — beperk ontkoppeling tot dit adres)
                  <input
                    type="email"
                    className="form-input"
                    value={testResetEmail}
                    onChange={(e) => setTestResetEmail(e.target.value)}
                    placeholder="omakoenders@…"
                  />
                </label>
                <div className="marketing-popup-test-actions">
                  <button type="button" className="btn btn-secondary btn-sm" disabled={diagnoseLoading} onClick={runDiagnose}>
                    Diagnose
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={diagnoseLoading || !testVisitorId.trim()}
                    onClick={unlinkTestVisitor}
                  >
                    Ontkoppel browser van e-mail
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={diagnoseLoading || (!testVisitorId.trim() && !testResetEmail.trim())}
                    onClick={resetTestVisitor}
                  >
                    Visitor test reset
                  </button>
                </div>
                {resetSummary?.ok && resetSummary.mode === 'unlink' ? (
                  <p className="muted marketing-popup-reset-summary">
                    Ontkoppeld: {resetSummary.removed?.order_visitor_links ?? 0} order-link(s), visitor op
                    inschrijving gewist {resetSummary.removed?.subscriber_visitor_cleared ?? 0}.
                    {resetSummary.emails_were_linked?.length
                      ? ` Was gekoppeld aan: ${resetSummary.emails_were_linked.join(', ')}.`
                      : ''}{' '}
                    {resetSummary.hint || ''}
                  </p>
                ) : null}
                {resetSummary?.ok && resetSummary.mode !== 'unlink' ? (
                  <p className="muted marketing-popup-reset-summary">
                    Gewist: pop-up state {resetSummary.removed?.popup_state ?? 0}, inschrijving visitor{' '}
                    {resetSummary.removed?.subscribers_by_visitor ?? 0}, per e-mail{' '}
                    {resetSummary.removed?.subscribers_by_email ?? 0}
                    {resetSummary.emails_cleared?.length
                      ? ` (${resetSummary.emails_cleared.join(', ')})`
                      : ''}
                  </p>
                ) : null}
                {diagnose ? (
                  <div className={`marketing-popup-diagnose ${diagnose.show ? 'ok' : 'block'}`} role="status">
                    <strong>{diagnose.show ? 'Zou tonen' : 'Toont niet'}</strong>
                    <p>{diagnose.hint}</p>
                    {diagnose.checks ? (
                      <ul className="marketing-popup-diagnose-list">
                        <li>Kopers: {diagnose.checks.show_for_returning_customers ? 'aan' : 'uit'}</li>
                        <li>Terugkerend: {diagnose.checks.is_returning_visitor ? 'ja' : 'nee'}</li>
                        <li>Ingeschreven: {diagnose.checks.subscribed_on_visitor ? 'ja' : 'nee'}</li>
                      </ul>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <aside className="marketing-popup-preview-col">
          <div className="marketing-popup-preview-frame">
            <span className="marketing-popup-preview-badge">Preview</span>
            <div className="marketing-popup-preview-head">
              <h3>
                <FiEye aria-hidden /> Shop-modal
              </h3>
              {tab !== 'copy' ? (
                <p className="muted">
                  Live: kopers {config?.show_for_returning_customers ? 'aan' : 'uit'} · gedrag{' '}
                  {config?.behavior_enabled ? 'aan' : 'uit'}
                </p>
              ) : null}
            </div>

            {tab !== 'copy' && config?.behavior_enabled ? (
              <div className="marketing-popup-segment marketing-popup-segment--wrap">
                <span className="marketing-popup-segment-label">Scenario</span>
                <button
                  type="button"
                  className={previewBehavior == null ? 'active' : ''}
                  onClick={() => setPreviewBehavior(null)}
                >
                  Standaard
                </button>
                <button
                  type="button"
                  className={previewBehavior === 'cart_abandon' ? 'active' : ''}
                  onClick={() => setPreviewBehavior('cart_abandon')}
                >
                  Mandje
                </button>
                <button
                  type="button"
                  className={previewBehavior === 'high_intent' ? 'active' : ''}
                  onClick={() => setPreviewBehavior('high_intent')}
                >
                  Views
                </button>
                <button
                  type="button"
                  className={previewBehavior === 'configurator' ? 'active' : ''}
                  onClick={() => setPreviewBehavior('configurator')}
                >
                  Configurator
                </button>
              </div>
            ) : null}

            {tab !== 'copy' ? (
              <>
                <div className="marketing-popup-segment">
                  <span className="marketing-popup-segment-label">Doelgroep</span>
                  <button
                    type="button"
                    className={previewVariant === 'welcome' ? 'active' : ''}
                    onClick={() => setPreviewVariant('welcome')}
                  >
                    Nieuw + code
                  </button>
                  <button
                    type="button"
                    className={previewVariant === 'newsletter' ? 'active' : ''}
                    onClick={() => setPreviewVariant('newsletter')}
                  >
                    Terug · nieuws
                  </button>
                  {(config?.returning_coupon_percent || 0) > 0 ||
                  (parseFloat(config?.returning_coupon_fixed_eur) || 0) > 0 ? (
                    <button
                      type="button"
                      className={previewVariant === 'returning_offer' ? 'active' : ''}
                      onClick={() => setPreviewVariant('returning_offer')}
                    >
                      Terug · code
                    </button>
                  ) : null}
                </div>

                <div className="marketing-popup-segment">
                  <span className="marketing-popup-segment-label">Scherm</span>
                  <button
                    type="button"
                    className={previewPhase === 'form' ? 'active' : ''}
                    onClick={() => setPreviewPhase('form')}
                  >
                    Form
                  </button>
                  <button
                    type="button"
                    className={previewPhase === 'success' ? 'active' : ''}
                    onClick={() => setPreviewPhase('success')}
                  >
                    Bedankt
                  </button>
                </div>
              </>
            ) : null}

            <div className="marketing-popup-segment">
              <span className="marketing-popup-segment-label">Device</span>
              <button
                type="button"
                className={previewDevice === 'desktop' ? 'active' : ''}
                onClick={() => setPreviewDevice('desktop')}
              >
                <FiMonitor aria-hidden /> Desktop
              </button>
              <button
                type="button"
                className={previewDevice === 'mobile' ? 'active' : ''}
                onClick={() => setPreviewDevice('mobile')}
              >
                <FiSmartphone aria-hidden /> Mobiel
              </button>
            </div>

            <MarketingPopupPreview
              device={previewDevice}
              variant={previewVariant}
              phase={previewPhase}
              config={previewConfig}
              behaviorSegment={tab !== 'copy' && config?.behavior_enabled ? previewBehavior : null}
              successCode={exampleCode}
              codeIsExample={previewPhase === 'success'}
            />

            {tab !== 'copy' ? (
              <CollapsibleHelp title="Preview uitleg" className="collapsible-help--compact">
                <p>
                  {config?.behavior_enabled
                    ? 'Kies Scenario voor gedragsteksten; Doelgroep = nieuw/terug + korting.'
                    : 'Zet Gedragsregels aan om scenario-preview (mandje, views, configurator) te zien.'}
                </p>
              </CollapsibleHelp>
            ) : null}
          </div>
        </aside>
      </div>

      <Klanten360Modal
        open={!!klanten360Modal.modalEmail}
        email={klanten360Modal.modalEmail}
        profile={klanten360Modal.modalProfile}
        loading={klanten360Modal.modalLoading}
        error={klanten360Modal.modalError}
        onClose={klanten360Modal.closeModal}
        onUnlinkVisitor={klanten360Modal.unlinkVisitor}
        unlinkBusyId={klanten360Modal.unlinkBusyId}
      />
    </div>
  );
}
